import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { field, ghost, primary, muted } from '../pages/time/styles';

// In-page replacements for window.prompt / window.confirm.
//   const { ask, confirm, dialogs } = useDialogs();
//   const reason = await ask({ title: 'Withdraw the exit?', message: '…', label: 'Reason', required: true });  // string, or null if cancelled
//   if (await confirm({ title, message, confirmText })) …
// Render {dialogs} once in the component's output.

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 1000 };
const box = { background: 'var(--card-bg, var(--bg-card, #fff))', color: 'var(--text-main)', border: '1px solid var(--border)', borderRadius: 10, padding: 20, width: 'min(460px, 100%)', display: 'grid', gap: 12, boxShadow: '0 10px 30px rgba(0,0,0,0.25)' };

const FOCUSABLE = 'input, textarea, select, button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])';

const Dialog = ({ spec, onClose }) => {
    const [value, setValue] = useState(spec.defaultValue || '');
    const [tried, setTried] = useState(false);
    const first = useRef(null);
    const formRef = useRef(null);
    const isPrompt = spec.kind === 'prompt';
    const blank = isPrompt && spec.required && !value.trim();
    const closeRef = useRef(onClose);
    useEffect(() => { closeRef.current = onClose; });
    useEffect(() => {
        const opener = document.activeElement; // focus goes back here when the dialog closes
        first.current?.focus();
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); closeRef.current(null); return; }
            if (e.key !== 'Tab' || !formRef.current) return;
            // Keep Tab inside the dialog.
            const items = [...formRef.current.querySelectorAll(FOCUSABLE)];
            if (!items.length) return;
            const firstEl = items[0];
            const lastEl = items[items.length - 1];
            if (!formRef.current.contains(document.activeElement)) { e.preventDefault(); firstEl.focus(); }
            else if (e.shiftKey && document.activeElement === firstEl) { e.preventDefault(); lastEl.focus(); }
            else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); firstEl.focus(); }
        };
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('keydown', onKey); if (opener && opener.isConnected) opener.focus?.(); };
    }, []);
    const submit = (e) => {
        e.preventDefault();
        e.stopPropagation(); // the dialog may sit inside another form
        if (blank) { setTried(true); first.current?.focus(); return; }
        onClose(isPrompt ? value.trim() : true);
    };
    const Input = spec.multiline ? 'textarea' : 'input';
    return createPortal(
        <div style={overlay}>
            <form ref={formRef} role="dialog" aria-modal="true" aria-labelledby="dlg-title" style={box} onSubmit={submit} noValidate>
                <h3 id="dlg-title" style={{ margin: 0, fontSize: 17 }}>{spec.title}</h3>
                {spec.message && <p style={{ ...muted, margin: 0, whiteSpace: 'pre-line' }}>{spec.message}</p>}
                {isPrompt && (
                    <label style={{ display: 'grid', gap: 4 }}>
                        <span style={muted}>{spec.label}{spec.required ? '' : ' (optional)'}</span>
                        <Input ref={first} style={field} type={spec.multiline ? undefined : (spec.type || 'text')} rows={spec.multiline ? 3 : undefined} value={value} aria-required={spec.required || undefined} aria-invalid={tried && blank ? true : undefined} onChange={(e) => setValue(e.target.value)} placeholder={spec.placeholder} />
                        {tried && blank && <span role="alert" style={{ color: 'var(--danger, #b42318)', fontSize: 13 }}>{spec.label} is needed.</span>}
                    </label>
                )}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button type="button" style={ghost} onClick={() => onClose(null)}>Cancel</button>
                    <button type="submit" className="btn btn-primary" style={primary} ref={isPrompt ? undefined : first}>{spec.confirmText || 'OK'}</button>
                </div>
            </form>
        </div>,
        document.body
    );
};

export const useDialogs = () => {
    const [spec, setSpec] = useState(null);
    const resolver = useRef(null);
    const open = useCallback((s) => new Promise((resolve) => { resolver.current = resolve; setSpec(s); }), []);
    const close = (result) => { const r = resolver.current; resolver.current = null; setSpec(null); if (r) r(result); };
    const ask = useCallback((s) => open({ kind: 'prompt', label: 'Reason', ...s }), [open]);
    const confirm = useCallback((s) => open({ kind: 'confirm', ...s }).then(r => r === true), [open]);
    return { ask, confirm, dialogs: spec ? <Dialog spec={spec} onClose={close} /> : null };
};
